import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Clock } from "lucide-react";

/**
 * Shop-owner dialog to set today's per-gram gold/silver rates.
 * Persisted on the shop document and surfaced on the customer catalog header.
 */
export default function GoldRateDialog({ open, onOpenChange, current, onSaved }) {
    const [g22, setG22] = useState("");
    const [g18, setG18] = useState("");
    const [gSilver, setGSilver] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setG22(current?.gold_rate_22k ?? "");
            setG18(current?.gold_rate_18k ?? "");
            setGSilver(current?.gold_rate_silver ?? "");
        }
    }, [open, current]);

    const updatedAgo = current?.gold_rate_updated_at
        ? new Date(current.gold_rate_updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
        : null;

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {};
            if (g22 !== "") payload.gold_rate_22k = parseFloat(g22) || 0;
            if (g18 !== "") payload.gold_rate_18k = parseFloat(g18) || 0;
            if (gSilver !== "") payload.gold_rate_silver = parseFloat(gSilver) || 0;
            if (!Object.keys(payload).length) {
                toast.warning("Nothing to update");
                setSaving(false);
                return;
            }
            const { data } = await api.patch("/owner/gold-rate", payload);
            toast.success("Today's rate saved");
            onSaved?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="gold-rate-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-editorial flex items-center gap-2">
                        <Coins className="w-5 h-5 text-brand-bronze" strokeWidth={1.5} />
                        Today&apos;s gold rate
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-brand-muted">
                    Enter rates per gram in INR. Prices on jewellery items will auto-calculate using these rates plus making charges.
                </p>

                <form onSubmit={save} className="space-y-4 mt-2" data-testid="gold-rate-form">
                    <div>
                        <label className="ayraa-overline">22K gold (₹ / gram)</label>
                        <input
                            type="number" min="0" step="0.01"
                            className="ayraa-input mt-1.5"
                            value={g22}
                            onChange={(e) => setG22(e.target.value)}
                            placeholder="e.g. 7250"
                            data-testid="gold-rate-22k-input"
                        />
                    </div>
                    <div>
                        <label className="ayraa-overline">18K gold (₹ / gram)</label>
                        <input
                            type="number" min="0" step="0.01"
                            className="ayraa-input mt-1.5"
                            value={g18}
                            onChange={(e) => setG18(e.target.value)}
                            placeholder="e.g. 5950"
                            data-testid="gold-rate-18k-input"
                        />
                    </div>
                    <div>
                        <label className="ayraa-overline">Silver (₹ / gram)</label>
                        <input
                            type="number" min="0" step="0.01"
                            className="ayraa-input mt-1.5"
                            value={gSilver}
                            onChange={(e) => setGSilver(e.target.value)}
                            placeholder="e.g. 95"
                            data-testid="gold-rate-silver-input"
                        />
                    </div>

                    {updatedAgo ? (
                        <div className="text-[11px] font-mono uppercase tracking-wider-2 text-brand-soft flex items-center gap-1.5">
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            Last updated · {updatedAgo}
                        </div>
                    ) : null}

                    <DialogFooter>
                        <button type="submit" className="ayraa-btn-primary w-full" disabled={saving} data-testid="gold-rate-save-btn">
                            {saving ? "Saving…" : "Save today's rate"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
